import React from 'react';
import type {IconAsset} from '@sail/icons/types';
import {SelectField, SelectFieldItem, Icon, view} from '@sail/ui';
import {COLORS} from 'src/v2/util/color';
import {Text} from './Text';

interface SelectableItem {
  id: string;
  label: string;
  description?: React.ReactNode;
  icon: IconAsset;
}

interface SelectProps<T extends SelectableItem> {
  items: T[];
  selectedItemId: string;
  onItemSelect: (itemId: string) => void;
  label?: string;
  placeholder?: string;
  size?: 'medium' | 'large';
  disabled?: boolean;
  disabledKeys?: Iterable<string>;
}

export const Select = <T extends SelectableItem>({
  items,
  selectedItemId,
  onItemSelect,
  label,
  placeholder,
  size = 'large',
  disabled,
  disabledKeys,
}: SelectProps<T>) => {
  return (
    <SelectField
      size={size}
      label={label}
      placeholder={placeholder}
      selectedKey={selectedItemId}
      hiddenElements={['label']}
      onSelectionChange={(value) => {
        onItemSelect(value as string);
      }}
      disabled={disabled}
      disabledKeys={disabledKeys}
      subviews={{
        button: {
          css: {
            backgroundColor: COLORS.offsetOnLight,
            border: 'none',
            borderRadius: 'small',
            boxShadow: 'none',
            ':hover:not(:active):not([aria-disabled="true"])': {
              backgroundColor: COLORS.offsetHoverOnLight,
            },
            ':focus': {
              backgroundColor: COLORS.offsetHoverOnLight,
            },
            ':active:not([aria-disabled="true"])': {
              backgroundColor: COLORS.offsetHoverOnLight,
            },
            '[aria-disabled="true"]': {
              backgroundColor: COLORS.offsetHoverOnLight,
            },
          },
        },
      }}
    >
      {items.map((item) => (
        <SelectFieldItem
          id={item.id}
          key={item.id}
          icon={<Icon icon={item.icon} css={{borderRadius: 'xsmall'}} />}
        >
          <view.div
            css={{
              stack: 'x',
              alignItems: 'center',
              distribute: 'space-between',
              width: 'fill',
            }}
          >
            <Text css={{color: 'inherit'}} type="bodyEmphasized">
              {item.label}
            </Text>
            {item.description ? item.description : null}
          </view.div>
        </SelectFieldItem>
      ))}
    </SelectField>
  );
};
